import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Package,
  Upload,
  Play,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  Filter,
  Monitor,
  Laptop,
  Smartphone,
  Tablet,
  Apple,
  Server,
  Trash2,
  Download,
  FileText,
  Calendar,
  Users,
  Target,
  AlertCircle,
  ChevronLeft,
  Info,
  Activity,
  Shield,
  Terminal,
  Code,
  Send,
  Pencil
} from 'lucide-react';
import { apiService, Device } from '../services/api';
import { deviceMatchesOS } from '../utils/devicePlatformMatch';
import DevicePicker, { getDeviceId as pickerGetDeviceId } from './DevicePicker';
import { STORAGE_APPLICATIONS, STORAGE_APP_DEPLOYMENTS } from '../constants/storageKeys';
import { HubPage, HubPanel, HubTabs } from './ui/HubShell';

function currentTenantScope(): string {
  try {
    const raw = localStorage.getItem('uem_user');
    if (!raw) return 'anon';
    const u = JSON.parse(raw);
    return String(u?.tenant_id || u?.tenant_code || 'anon');
  } catch {
    return 'anon';
  }
}

function applicationsStorageKey(): string {
  return `${STORAGE_APPLICATIONS}:${currentTenantScope()}`;
}

function deploymentsStorageKey(): string {
  return `${STORAGE_APP_DEPLOYMENTS}:${currentTenantScope()}`;
}

function parseAppList(raw: string | null): Application[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function parseDepList(raw: string | null): Deployment[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Exact demo seeds only — never drop user-created apps (e.g. Osquery). */
const MOCK_APP_FINGERPRINTS = [
  { id: 'app-1', name: 'Google Chrome' },
  { id: 'app-2', name: 'VS Code' },
  { id: 'app-3', name: 'Slack' },
  { id: 'app-4', name: 'Firefox' },
];
const MOCK_DEP_IDS = new Set(['dep-1', 'dep-2']);

function isMockApplication(app: Application): boolean {
  return MOCK_APP_FINGERPRINTS.some(
    (m) => m.id === app.id && String(app.name || '').trim() === m.name
  );
}

function isMockDeployment(dep: Deployment): boolean {
  if (MOCK_DEP_IDS.has(dep.id)) return true;
  // Drop deployments that only pointed at demo apps
  return MOCK_APP_FINGERPRINTS.some((m) => m.id === dep.applicationId);
}

function mergeById<T extends { id: string }>(lists: T[][]): T[] {
  const map = new Map<string, T>();
  for (const list of lists) {
    for (const item of list) {
      if (!item?.id) continue;
      if (!map.has(item.id)) map.set(item.id, item);
    }
  }
  return Array.from(map.values());
}

/**
 * Recover apps from the current tenant key + legacy unscoped key.
 * Does not pull other tenants' catalogs.
 */
function recoverAllStoredApplications(): Application[] {
  const lists: Application[][] = [];
  try {
    lists.push(parseAppList(localStorage.getItem(applicationsStorageKey())));
    lists.push(parseAppList(localStorage.getItem(STORAGE_APPLICATIONS)));
  } catch {
    /* ignore */
  }
  return mergeById(lists).filter((a) => !isMockApplication(a));
}

function recoverAllStoredDeployments(): Deployment[] {
  const lists: Deployment[][] = [];
  try {
    lists.push(parseDepList(localStorage.getItem(deploymentsStorageKey())));
    lists.push(parseDepList(localStorage.getItem(STORAGE_APP_DEPLOYMENTS)));
  } catch {
    /* ignore */
  }
  return mergeById(lists).filter((d) => !isMockDeployment(d));
}

function saveStoredApplications(apps: Application[]) {
  try {
    localStorage.setItem(applicationsStorageKey(), JSON.stringify(apps));
  } catch {
    /* ignore quota / private mode */
  }
}

function saveStoredDeployments(deps: Deployment[]) {
  try {
    localStorage.setItem(deploymentsStorageKey(), JSON.stringify(deps));
  } catch {
    /* ignore quota / private mode */
  }
}

function currentUserEmail(): string {
  try {
    const raw = localStorage.getItem('uem_user');
    if (!raw) return 'unknown';
    const u = JSON.parse(raw);
    return String(u?.email || u?.username || 'unknown');
  } catch {
    return 'unknown';
  }
}

interface Application {
  id: string;
  name: string;
  version: string;
  platform: 'windows' | 'macos' | 'linux' | 'android' | 'ios';
  fileType: string;
  fileSize: number;
  description?: string;
  uploadedAt: string;
  uploadedBy: string;
  status: 'active' | 'archived';
  // Deployment Configuration
  deploymentMethod: 'file' | 'store' | 'winget' | 'homebrew' | 'package-manager';
  installerUrl?: string; // URL to download installer file
  relativePath?: string;
  installCommand?: string; // Platform-specific install command/arguments
  silentArgs?: string; // Silent installation arguments (e.g., /S, /qn, /silent)
  checkCondition?: {
    type: 'file_path' | 'registry_key' | 'package_installed' | 'process_running';
    value: string; // Path, registry key, package name, or process name
  };
  // Store App IDs (for Android/iOS)
  storeAppId?: string; // Package name (Android) or iTunes Store ID (iOS)
  // Platform-specific options
  wingetId?: string; // For Windows Winget deployment
  homebrewFormula?: string; // For macOS Homebrew deployment
  packageName?: string; // For Linux apt/yum packages
  linuxDistro?: 'debian' | 'rhel' | 'ubuntu' | 'centos'; // Linux distribution type
}

interface DeviceGroup {
  id: string;
  name: string;
  type?: string;
  children?: DeviceGroup[];
  folderId?: string;
}

interface Deployment {
  id: string;
  applicationId: string;
  applicationName: string;
  targetType: 'device' | 'group' | 'platform';
  targetId: string;
  targetName: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  scheduledAt?: string;
  startedAt?: string;
  completedAt?: string;
  progress: number;
  totalDevices: number;
  successfulDevices: number;
  failedDevices: number;
  createdBy: string;
  createdAt: string;
}

interface ApplicationDeploymentProps {
  deviceGroups?: DeviceGroup[];
}

const ApplicationDeployment = ({ deviceGroups = [] }: ApplicationDeploymentProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'applications' | 'deployments' | 'scripts'>('applications');
  const [applications, setApplications] = useState<Application[]>([]);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDeviceType, setSelectedDeviceType] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingApp, setEditingApp] = useState<Application | null>(null);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [selectedDeploymentDetails, setSelectedDeploymentDetails] = useState<any | null>(null);
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [devices, setDevices] = useState<any[]>([]);
  const [packageSettings, setPackageSettings] = useState<any>(null);

  const openDeploymentDetails = async (deployment: Deployment) => {
    setShowDetailsModal(true);
    setDetailsLoading(true);
    setDetailsError(null);
    setSelectedDeploymentDetails(null);
    try {
      const detail = await apiService.getTenantAppDeployment(deployment.id);
      setSelectedDeploymentDetails(detail || { ...deployment, deviceResults: [] });
    } catch (err: any) {
      // Fall back to list row data so the modal still opens
      setSelectedDeploymentDetails({ ...deployment, deviceResults: [] });
      setDetailsError(err?.message || 'Could not load full deployment details');
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    apiService
      .getDevices()
      .then((data) => {
        if (!cancelled) setDevices(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setDevices([]);
      });
    apiService
      .getTenantPackageSettings()
      .then((s) => {
        if (!cancelled) setPackageSettings(s);
      })
      .catch(() => {
        if (!cancelled) setPackageSettings(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Companion Support Tickets can hand off here: open Windows/software apps and
  // prefill search from the ticket subject (e.g. "firefox").
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('uem_app_deploy_hint');
      if (!raw) return;
      sessionStorage.removeItem('uem_app_deploy_hint');
      const hint = JSON.parse(raw) as {
        search?: string;
        platform?: string;
        device_name?: string | null;
      };
      const platform = String(hint.platform || 'windows').toLowerCase();
      if (['windows', 'linux', 'macos', 'android', 'ios'].includes(platform)) {
        setSelectedDeviceType(platform);
      }
      if (hint.search) setSearchTerm(hint.search);
    } catch {
      /* ignore bad hint */
    }
  }, []);

  const refreshCatalog = useCallback(async () => {
    try {
      const [apps, deps] = await Promise.all([
        apiService.listTenantApps(),
        apiService.listTenantAppDeployments(),
      ]);
      setApplications(Array.isArray(apps) ? apps : []);
      setDeployments(Array.isArray(deps) ? deps : []);
    } catch (e) {
      console.error('Failed to load app catalog', e);
      // Fallback: recover any legacy localStorage apps once
      const legacy = recoverAllStoredApplications();
      if (legacy.length) setApplications(legacy);
    }
  }, []);

  useEffect(() => {
    refreshCatalog();
  }, [refreshCatalog]);

  // Poll in-progress deployments
  useEffect(() => {
    const hasActive = deployments.some((d) => d.status === 'pending' || d.status === 'in_progress');
    if (!hasActive) return;
    const t = window.setInterval(() => {
      apiService.listTenantAppDeployments().then((deps) => {
        if (Array.isArray(deps)) setDeployments(deps);
      }).catch(() => {});
    }, 5000);
    return () => window.clearInterval(t);
  }, [deployments]);

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'windows':
        return <Monitor size={18} className="text-blue-500" />;
      case 'macos':
        return <Apple size={18} className="text-slate-600" />;
      case 'linux':
        return <Server size={18} className="text-orange-500" />;
      case 'android':
        return <Smartphone size={18} className="text-green-500" />;
      case 'ios':
        return <Tablet size={18} className="text-gray-500" />;
      default:
        return <Package size={18} className="text-slate-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      in_progress: 'bg-blue-100 text-blue-700 border-blue-300',
      completed: 'bg-green-100 text-green-700 border-green-300',
      failed: 'bg-red-100 text-red-700 border-red-300',
      active: 'bg-green-100 text-green-700 border-green-300',
      archived: 'bg-slate-100 text-slate-700 border-slate-300'
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium border ${styles[status as keyof typeof styles] || styles.pending}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  // Organize applications by device type
  const organizeByDeviceType = () => {
    const organized: { [key: string]: Application[] } = {
      windows: [],
      android: [],
      macos: [],
      linux: [],
      ios: []
    };

    applications.forEach(app => {
      const platform = app.platform.toLowerCase();
      if (organized[platform]) {
        organized[platform].push(app);
      }
    });

    return organized;
  };

  const organizedApps = organizeByDeviceType();

  // Extract departments and platforms from deviceGroups
  const deptCategory = deviceGroups?.[0]?.children?.find((c: DeviceGroup) => c.id === 'cat-depts');
  const platformCategory = deviceGroups?.[0]?.children?.find((c: DeviceGroup) => c.id === 'cat-platforms');
  const departments = deptCategory?.children || [];
  const platforms = platformCategory?.children || [];

  // Get platform map for OS filtering
  const platformMap: { [key: string]: string } = {
    'windows': 'plat-windows',
    'android': 'plat-android',
    'macos': 'plat-macos',
    'linux': 'plat-linux',
    'ios': 'plat-ios'
  };
  const requiredPlatform = selectedDeviceType ? platformMap[selectedDeviceType] : null;

  const deviceTypes = [
    {
      id: 'windows',
      name: 'Windows',
      icon: Monitor,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      description: 'Windows applications (MSI, EXE)'
    },
    {
      id: 'android',
      name: 'Android',
      icon: Smartphone,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      description: 'Android applications (APK)'
    },
    {
      id: 'macos',
      name: 'macOS',
      icon: Apple,
      color: 'text-slate-600',
      bgColor: 'bg-slate-50',
      borderColor: 'border-slate-200',
      description: 'macOS applications (DMG, PKG)'
    },
    {
      id: 'linux',
      name: 'Linux',
      icon: Server,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      description: 'Linux packages (DEB, RPM)'
    },
    {
      id: 'ios',
      name: 'iOS',
      icon: Tablet,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      description: 'iOS applications (IPA)'
    }
  ];

  // Filter applications based on selected device type and search term
  const filteredApplications = selectedDeviceType
    ? organizedApps[selectedDeviceType]?.filter(app => {
        const matchesSearch = app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             app.version.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             app.description?.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
      }) || []
    : []; // Show empty when no device type is selected (like policies page)

  return (
    <div className="space-y-6">
      <HubPage
        title={t('navigation.applicationDeployment')}
        subtitle={t('applicationDeployment.description', 'Manage and deploy applications across your device fleet')}
        icon={<Package size={20} />}
        eyebrow="Fleet"
        action={
          <button
            type="button"
            onClick={() => {
              setEditingApp(null);
              setShowUploadModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <Upload size={18} />
            {t('applicationDeployment.uploadApplication', 'Upload Application')}
          </button>
        }
      >
        <HubPanel withTabs padded={false}>
          <HubTabs
            value={activeTab}
            onChange={setActiveTab}
            items={[
              { id: 'applications', label: `${t('applicationDeployment.applications', 'Applications')} (${applications.length})`, icon: <Package size={15} /> },
              { id: 'deployments', label: `${t('applicationDeployment.deployments', 'Deployments')} (${deployments.length})`, icon: <Play size={15} /> },
              { id: 'scripts', label: t('applicationDeployment.scripts', 'Scripts'), icon: <Terminal size={15} /> },
            ]}
          />
          <div className="hub-panel__inner">
      {/* Applications Tab */}
      {activeTab === 'applications' && (
        <div className="space-y-6">
          {/* Device Type Cards - Always Visible */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {deviceTypes.map((deviceType) => {
              const Icon = deviceType.icon;
              const appsForType = organizedApps[deviceType.id] || [];
              const isSelected = selectedDeviceType === deviceType.id;

              return (
                <div
                  key={deviceType.id}
                  onClick={() => setSelectedDeviceType(isSelected ? null : deviceType.id)}
                  className={`${deviceType.bgColor} ${deviceType.borderColor} border-2 rounded-lg p-6 cursor-pointer transition-all hover:shadow-lg ${
                    isSelected ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`${deviceType.color} p-3 rounded-lg bg-white`}>
                      <Icon className="w-8 h-8" />
                    </div>
                    <span className="px-3 py-1 bg-white rounded-full text-sm font-semibold text-gray-700">
                      {appsForType.length}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{deviceType.name}</h3>
                  <p className="text-sm text-gray-600">{deviceType.description}</p>
                  {appsForType.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-2">Available Applications:</p>
                      <div className="space-y-1">
                        {appsForType.slice(0, 2).map((app) => (
                          <div key={app.id} className="text-sm font-medium text-gray-700 truncate">
                            {app.name}
                          </div>
                        ))}
                        {appsForType.length > 2 && (
                          <div className="text-xs text-gray-500">
                            +{appsForType.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Selected Device Type Applications */}
          {selectedDeviceType ? (
            <div className="bg-white rounded-lg shadow border">
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center">
                  {(() => {
                    const deviceType = deviceTypes.find(d => d.id === selectedDeviceType);
                    const Icon = deviceType?.icon || Package;
                    return (
                      <>
                        <Icon className={`w-5 h-5 mr-2 ${deviceType?.color || 'text-gray-600'}`} />
                        <h3 className="text-lg font-semibold">
                          {deviceType?.name} Applications ({filteredApplications.length})
                        </h3>
                      </>
                    );
                  })()}
                </div>
                <button
                  onClick={() => {
                    setEditingApp(null);
                    setShowUploadModal(true);
                  }}
                  className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 flex items-center"
                >
                  <Upload className="w-4 h-4 mr-1" />
                  Upload Application
                </button>
              </div>

              <div className="p-4">
                {/* Search Filter - OS Specific */}
                <div className="mb-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type="text"
                      placeholder={`Search ${deviceTypes.find(d => d.id === selectedDeviceType)?.name} applications...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>

                {filteredApplications.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-gray-500 mb-4">No applications for {deviceTypes.find(d => d.id === selectedDeviceType)?.name} devices</p>
                    <button
                      onClick={() => {
                        setEditingApp(null);
                        setShowUploadModal(true);
                      }}
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Upload First Application
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredApplications.map((app) => (
                      <div
                        key={app.id}
                        className="bg-white rounded-lg shadow-sm border border-slate-200 p-5 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            {getPlatformIcon(app.platform)}
                            <div>
                              <h3 className="font-semibold text-slate-800">{app.name}</h3>
                              <p className="text-xs text-slate-500">v{app.version}</p>
                            </div>
                          </div>
                          {getStatusBadge(app.status)}
                        </div>

                        {app.description && (
                          <p className="text-sm text-slate-600 mb-3 line-clamp-2">{app.description}</p>
                        )}

                        <div className="flex items-center justify-between text-xs text-slate-500 mb-4">
                          <span className="flex items-center gap-1">
                            <FileText size={12} />
                            {app.fileType.toUpperCase()} • {(app.fileSize).toFixed(1)} MB
                          </span>
                          <span className="capitalize">{app.platform}</span>
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setSelectedApp(app);
                              setShowDeployModal(true);
                            }}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                          >
                            <Play size={14} />
                            Deploy
                          </button>
                          <button
                            type="button"
                            title="Edit application"
                            onClick={() => {
                              setEditingApp(app);
                              setShowUploadModal(true);
                            }}
                            className="px-3 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                          >
                            <Pencil size={16} className="text-slate-600" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow border p-10 text-center">
              <Monitor className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-700 font-medium mb-1">Select a platform to view applications</p>
              <p className="text-sm text-slate-500">
                Click Windows, Android, macOS, Linux, or iOS above. MSI packages appear under Windows.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Deployments Tab */}
      {activeTab === 'deployments' && (
        <>
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-800">Deployment History</h3>
                <div className="flex gap-2">
                  <button className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-white transition-colors">
                    <Filter size={14} className="inline mr-1" />
                    Filter
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 font-semibold text-slate-700">Application</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Target</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Status</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Progress</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Devices</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Started</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {deployments.map((deployment) => (
                    <tr key={deployment.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{deployment.applicationName}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Target size={14} className="text-slate-400" />
                          <span className="text-slate-600">{deployment.targetName}</span>
                          <span className="text-xs text-slate-400">({deployment.targetType})</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(deployment.status)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all ${
                                deployment.status === 'failed'
                                  ? 'bg-red-500'
                                  : deployment.status === 'completed' && deployment.failedDevices > 0
                                    ? 'bg-amber-500'
                                    : 'bg-blue-600'
                              }`}
                              style={{ width: `${deployment.progress}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-slate-600 w-10">{deployment.progress}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <span className="text-green-600 font-medium">{deployment.successfulDevices}</span>
                          {deployment.failedDevices > 0 && (
                            <span className="text-red-600 ml-1">/{deployment.failedDevices}</span>
                          )}
                          <span className="text-slate-400">/{deployment.totalDevices}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {deployment.startedAt
                          ? new Date(deployment.startedAt).toLocaleDateString()
                          : 'Scheduled'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => openDeploymentDetails(deployment)}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Deployment Status Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Activity size={20} className="text-blue-600" />
              Deployment Status
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Deployments */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Total Deployments</span>
                  <Play size={18} className="text-blue-600" />
                </div>
                <p className="text-2xl font-bold text-slate-800">{deployments.length}</p>
              </div>

              {/* Completed */}
              <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Completed</span>
                  <CheckCircle size={18} className="text-green-600" />
                </div>
                <p className="text-2xl font-bold text-slate-800">
                  {deployments.filter(d => d.status === 'completed').length}
                </p>
              </div>

              {/* In Progress */}
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">In Progress</span>
                  <Clock size={18} className="text-yellow-600" />
                </div>
                <p className="text-2xl font-bold text-slate-800">
                  {deployments.filter(d => d.status === 'in_progress').length}
                </p>
              </div>

              {/* Failed */}
              <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-600">Failed</span>
                  <XCircle size={18} className="text-red-600" />
                </div>
                <p className="text-2xl font-bold text-slate-800">
                  {deployments.filter(d => d.status === 'failed').length}
                </p>
              </div>
            </div>

            {/* Overall Statistics */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Total Devices Targeted</p>
                  <p className="text-xl font-semibold text-slate-800">
                    {deployments.reduce((sum, d) => sum + d.totalDevices, 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Successful Installs</p>
                  <p className="text-xl font-semibold text-green-600">
                    {deployments.reduce((sum, d) => sum + d.successfulDevices, 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Failed Installs</p>
                  <p className="text-xl font-semibold text-red-600">
                    {deployments.reduce((sum, d) => sum + d.failedDevices, 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Deployed Applications & Collections Information */}
      {(activeTab === 'applications' || activeTab === 'deployments') && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Package size={20} className="text-blue-600" />
            Deployed Applications & Collections
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Deployed Applications Summary */}
            <div className="bg-slate-50 rounded-lg p-5 border border-slate-200">
              <h4 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <Play size={16} className="text-blue-600" />
                Deployed Applications
              </h4>
              <div className="space-y-3">
                {deployments.length > 0 ? (
                  deployments.map((deployment) => (
                    <div key={deployment.id} className="bg-white rounded-lg p-4 border border-slate-200">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h5 className="font-semibold text-slate-800">{deployment.applicationName}</h5>
                          <p className="text-xs text-slate-500 mt-1">
                            Deployed to: <span className="font-medium">{deployment.targetName}</span>
                          </p>
                        </div>
                        {getStatusBadge(deployment.status)}
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                        <div className="flex items-center gap-4 text-xs text-slate-600">
                          <span className="flex items-center gap-1">
                            <CheckCircle size={12} className="text-green-600" />
                            {deployment.successfulDevices} success
                          </span>
                          {deployment.failedDevices > 0 && (
                            <span className="flex items-center gap-1">
                              <XCircle size={12} className="text-red-600" />
                              {deployment.failedDevices} failed
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Target size={12} className="text-slate-400" />
                            {deployment.totalDevices} total
                          </span>
                        </div>
                        <div className="text-xs text-slate-500">
                          {deployment.startedAt
                            ? new Date(deployment.startedAt).toLocaleDateString()
                            : 'Scheduled'}
                        </div>
                      </div>
                      <div className="mt-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-200 rounded-full h-1.5">
                            <div
                              className={`h-1.5 rounded-full transition-all ${
                                deployment.status === 'failed'
                                  ? 'bg-red-500'
                                  : deployment.status === 'completed' && deployment.failedDevices > 0
                                    ? 'bg-amber-500'
                                    : 'bg-blue-600'
                              }`}
                              style={{ width: `${deployment.progress}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-slate-600 w-10">{deployment.progress}%</span>
                        </div>
                      </div>
                      <div className="mt-3 pt-2 border-t border-slate-100">
                        <button
                          type="button"
                          onClick={() => openDeploymentDetails(deployment)}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Package size={32} className="mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">No deployments yet</p>
                    <p className="text-xs mt-1">Deploy applications to see them here</p>
                  </div>
                )}
              </div>
            </div>

            {/* Collections Information */}
            <div className="bg-slate-50 rounded-lg p-5 border border-slate-200">
              <h4 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <Users size={16} className="text-green-600" />
                Target Collections
              </h4>
              <div className="space-y-3">
                {deployments.length > 0 ? (
                  (() => {
                    // Get unique collections from deployments
                    const uniqueCollections = Array.from(
                      new Map(
                        deployments
                          .filter(d => d.targetType === 'group' || d.targetType === 'platform')
                          .map(d => [d.targetId, d.targetName])
                      ).entries()
                    ).map(([id, name]) => ({ id, name }));

                    return uniqueCollections.length > 0 ? (
                      uniqueCollections.map((collection) => {
                        const collectionDeployments = deployments.filter(
                          d => d.targetId === collection.id
                        );
                        const totalDevices = collectionDeployments.reduce(
                          (sum, d) => sum + d.totalDevices, 0
                        );
                        const successfulDevices = collectionDeployments.reduce(
                          (sum, d) => sum + d.successfulDevices, 0
                        );
                        const failedDevices = collectionDeployments.reduce(
                          (sum, d) => sum + d.failedDevices, 0
                        );

                        return (
                          <div key={collection.id} className="bg-white rounded-lg p-4 border border-slate-200">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <h5 className="font-semibold text-slate-800 flex items-center gap-2">
                                  <Target size={14} className="text-blue-600" />
                                  {collection.name}
                                </h5>
                                <p className="text-xs text-slate-500 mt-1">
                                  {collectionDeployments.length} deployment{collectionDeployments.length !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="text-center">
                                  <p className="text-slate-500">Total</p>
                                  <p className="font-semibold text-slate-800">{totalDevices}</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-slate-500">Success</p>
                                  <p className="font-semibold text-green-600">{successfulDevices}</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-slate-500">Failed</p>
                                  <p className="font-semibold text-red-600">{failedDevices}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <Users size={32} className="mx-auto mb-2 text-slate-300" />
                        <p className="text-sm">No collections targeted</p>
                        <p className="text-xs mt-1">Collections will appear here after deployment</p>
                      </div>
                    );
                  })()
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Users size={32} className="mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">No collections targeted</p>
                    <p className="text-xs mt-1">Collections will appear here after deployment</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'scripts' && <RunCustomScriptTab />}

          </div>
        </HubPanel>
      </HubPage>

      {/* Upload / Edit Application Modal */}
      {showUploadModal && (
        <ApplicationUploadModal
          initialPlatform={(selectedDeviceType as Application['platform']) || 'windows'}
          packageSettings={packageSettings}
          editingApp={editingApp}
          onClose={() => {
            setShowUploadModal(false);
            setEditingApp(null);
          }}
          onUpload={async () => {
            await refreshCatalog();
            setActiveTab('applications');
            setSearchTerm('');
            setShowUploadModal(false);
            setEditingApp(null);
          }}
        />
      )}

      {/* Deploy Application Modal */}
      {showDeployModal && selectedApp && (
        <DeploymentModal
          application={selectedApp}
          deviceGroups={deviceGroups}
          devices={devices}
          onClose={() => {
            setShowDeployModal(false);
            setSelectedApp(null);
          }}
          onDeploy={async () => {
            await refreshCatalog();
            setShowDeployModal(false);
            setSelectedApp(null);
            setActiveTab('deployments');
          }}
        />
      )}

      {/* Deployment Details Modal */}
      {showDetailsModal && (
        <DeploymentDetailsModal
          loading={detailsLoading}
          error={detailsError}
          deployment={selectedDeploymentDetails}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedDeploymentDetails(null);
            setDetailsError(null);
          }}
        />
      )}
    </div>
  );
};

function deviceResultStatusBadge(status: string) {
  const s = (status || 'pending').toLowerCase();
  const styles: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    in_progress: 'bg-blue-100 text-blue-700 border-blue-300',
    running: 'bg-blue-100 text-blue-700 border-blue-300',
    completed: 'bg-green-100 text-green-700 border-green-300',
    success: 'bg-green-100 text-green-700 border-green-300',
    succeeded: 'bg-green-100 text-green-700 border-green-300',
    failed: 'bg-red-100 text-red-700 border-red-300',
    error: 'bg-red-100 text-red-700 border-red-300',
    timeout: 'bg-red-100 text-red-700 border-red-300',
    missing: 'bg-slate-100 text-slate-700 border-slate-300',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${styles[s] || styles.pending}`}>
      {s.replace('_', ' ').toUpperCase()}
    </span>
  );
}

const DeploymentDetailsModal = ({
  deployment,
  loading,
  error,
  onClose,
}: {
  deployment: any | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}) => {
  const deviceResults: any[] = Array.isArray(deployment?.deviceResults) ? deployment.deviceResults : [];
  const failedCount = deviceResults.filter((r) =>
    ['failed', 'error', 'timeout', 'cancelled'].includes(String(r.status || '').toLowerCase())
  ).length;

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="bg-slate-50 border-b border-slate-200 p-4 flex justify-between items-center shrink-0">
          <div>
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Activity size={18} className="text-blue-600" />
              Deployment Details
            </h3>
            {deployment?.applicationName && (
              <p className="text-sm text-slate-500 mt-0.5">{deployment.applicationName}</p>
            )}
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <XCircle size={20} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {loading && (
            <div className="text-center py-10 text-slate-500 text-sm">Loading deployment details…</div>
          )}

          {!loading && error && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!loading && deployment && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs text-slate-500 mb-1">Status</div>
                  <div className="text-sm font-semibold text-slate-800 capitalize">
                    {String(deployment.status || 'unknown').replace('_', ' ')}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs text-slate-500 mb-1">Target</div>
                  <div className="text-sm font-semibold text-slate-800 truncate" title={deployment.targetName}>
                    {deployment.targetName || '—'}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs text-slate-500 mb-1">Devices</div>
                  <div className="text-sm font-semibold text-slate-800">
                    <span className="text-green-600">{deployment.successfulDevices ?? 0}</span>
                    <span className="text-red-600"> / {deployment.failedDevices ?? failedCount}</span>
                    <span className="text-slate-400"> / {deployment.totalDevices ?? deviceResults.length}</span>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-xs text-slate-500 mb-1">Started</div>
                  <div className="text-sm font-semibold text-slate-800">
                    {deployment.startedAt
                      ? new Date(deployment.startedAt).toLocaleString()
                      : '—'}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-2">
                  <Monitor size={14} className="text-slate-500" />
                  Per-device results
                </h4>
                {deviceResults.length === 0 ? (
                  <div className="rounded-lg border border-slate-200 p-4 text-sm text-slate-500 text-center">
                    No device command results available for this deployment.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {deviceResults.map((r) => {
                      const isFailed = ['failed', 'error', 'timeout', 'cancelled', 'missing'].includes(
                        String(r.status || '').toLowerCase()
                      );
                      return (
                        <div
                          key={r.commandId || r.deviceId || r.deviceName}
                          className={`rounded-lg border p-3 ${
                            isFailed ? 'border-red-200 bg-red-50/40' : 'border-slate-200 bg-white'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="min-w-0">
                              <div className="font-medium text-slate-800 truncate">{r.deviceName || 'Unknown device'}</div>
                              {r.deviceId && (
                                <div className="text-xs text-slate-400 font-mono truncate">{r.deviceId}</div>
                              )}
                            </div>
                            {deviceResultStatusBadge(r.status)}
                          </div>
                          {isFailed && (
                            <div className="mt-2 rounded-md bg-white border border-red-100 p-2.5">
                              <div className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1">
                                <AlertCircle size={12} />
                                Failure reason
                              </div>
                              <pre className="text-xs text-red-800 whitespace-pre-wrap break-words font-sans">
                                {r.errorMessage || 'Install failed (no error details returned by agent).'}
                              </pre>
                            </div>
                          )}
                          {r.command && (
                            <details className="mt-2">
                              <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                                Install command
                              </summary>
                              <pre className="mt-1 text-xs bg-slate-900 text-slate-100 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
                                {r.command}
                              </pre>
                            </details>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="border-t border-slate-200 p-4 flex justify-end shrink-0 bg-slate-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Upload / Edit Modal Component
const ApplicationUploadModal = ({
  onClose,
  onUpload,
  initialPlatform = 'windows',
  packageSettings = null,
  editingApp = null,
}: {
  onClose: () => void;
  onUpload: (app?: Application) => void | Promise<void>;
  initialPlatform?: Application['platform'];
  packageSettings?: any;
  editingApp?: Application | null;
}) => {
  const isEdit = Boolean(editingApp?.id);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState<{
    name: string;
    version: string;
    platform: 'windows' | 'macos' | 'linux' | 'android' | 'ios';
    description: string;
    file: File | null;
    deploymentMethod: 'file' | 'store' | 'winget' | 'homebrew' | 'package-manager';
    installerUrl: string;
    relativePath: string;
    installCommand: string;
    silentArgs: string;
    checkConditionType: 'file_path' | 'registry_key' | 'package_installed' | 'process_running' | '';
    checkConditionValue: string;
    storeAppId: string;
    wingetId: string;
    homebrewFormula: string;
    packageName: string;
    linuxDistro: 'debian' | 'rhel' | 'ubuntu' | 'centos' | '';
  }>({
    name: editingApp?.name || '',
    version: editingApp?.version || '',
    platform: editingApp?.platform || initialPlatform,
    description: editingApp?.description || '',
    file: null,
    deploymentMethod: editingApp?.deploymentMethod || 'file',
    installerUrl: editingApp?.installerUrl || '',
    relativePath: editingApp?.relativePath || '',
    installCommand: editingApp?.installCommand || '',
    silentArgs: editingApp?.silentArgs || '',
    checkConditionType: (editingApp?.checkCondition?.type as any) || '',
    checkConditionValue: editingApp?.checkCondition?.value || '',
    storeAppId: editingApp?.storeAppId || '',
    wingetId: editingApp?.wingetId || '',
    homebrewFormula: editingApp?.homebrewFormula || '',
    packageName: editingApp?.packageName || '',
    linuxDistro: (editingApp?.linuxDistro as any) || '',
  });

  const [storeSearchTerm, setStoreSearchTerm] = useState('');
  const [storeSearchResults, setStoreSearchResults] = useState<any[]>([]);
  const [searchingStore, setSearchingStore] = useState(false);

  // Search iOS App Store
  const searchiOSStore = async (term: string) => {
    if (!term.trim()) return;
    setSearchingStore(true);
    try {
      const response = await fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(term)}&entity=software&limit=10`);
      const data = await response.json();
      setStoreSearchResults(data.results || []);
    } catch (error) {
      console.error('Failed to search iOS store:', error);
      setStoreSearchResults([]);
    } finally {
      setSearchingStore(false);
    }
  };

  // Search Android Play Store (using a proxy/search API)
  const searchAndroidStore = async (term: string) => {
    if (!term.trim()) return;
    setSearchingStore(true);
    // Note: Direct Play Store API requires authentication
    // For now, we'll use a placeholder - in production, use Google Play Developer API
    setStoreSearchResults([]);
    setSearchingStore(false);
  };

  const handleStoreSearch = () => {
    if (formData.platform === 'ios') {
      searchiOSStore(storeSearchTerm);
    } else if (formData.platform === 'android') {
      searchAndroidStore(storeSearchTerm);
    }
  };

  const selectStoreApp = (app: any) => {
    if (formData.platform === 'ios') {
      setFormData({
        ...formData,
        name: app.trackName || app.collectionName,
        version: app.version || '',
        storeAppId: app.trackId?.toString() || '',
        deploymentMethod: 'store'
      });
      setStoreSearchResults([]);
      setStoreSearchTerm('');
    } else if (formData.platform === 'android') {
      setFormData({
        ...formData,
        name: app.title || app.name,
        version: app.version || '',
        storeAppId: app.packageName || app.appId || '',
        deploymentMethod: 'store'
      });
      setStoreSearchResults([]);
      setStoreSearchTerm('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate based on deployment method
    if (formData.deploymentMethod === 'store') {
      if (!formData.storeAppId) {
        window.alert('Please select an app from the store search results');
        return;
      }
    } else if (formData.deploymentMethod === 'winget') {
      if (!formData.wingetId) {
        window.alert('Please enter a Winget package ID');
        return;
      }
    } else if (formData.deploymentMethod === 'homebrew') {
      if (!formData.homebrewFormula) {
        window.alert('Please enter a Homebrew formula name');
        return;
      }
    } else if (formData.deploymentMethod === 'package-manager') {
      if (!formData.packageName) {
        window.alert('Please enter a package name');
        return;
      }
    } else {
      const uemHosted = !packageSettings || packageSettings.source_mode === 'uem_hosted';
      const hasExistingPackage =
        isEdit && Boolean(editingApp?.relativePath || editingApp?.installerUrl || editingApp?.fileType);
      if (uemHosted && !formData.file && !formData.installerUrl && !hasExistingPackage && !formData.relativePath) {
        window.alert('Please upload a file or provide an installer URL');
        return;
      }
      if (!uemHosted && !formData.relativePath && !formData.installerUrl && !formData.file && !hasExistingPackage) {
        window.alert('Enter a path relative to your share, or an installer URL');
        return;
      }
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('version', formData.version || '');
      fd.append('platform', formData.platform);
      fd.append('description', formData.description || '');
      fd.append('deployment_method', formData.deploymentMethod);
      fd.append('silent_args', formData.silentArgs || '');
      fd.append('install_command', formData.installCommand || '');
      fd.append('relative_path', formData.relativePath || '');
      fd.append('installer_url', formData.installerUrl || '');
      fd.append('store_app_id', formData.storeAppId || '');
      fd.append('winget_id', formData.wingetId || '');
      fd.append('homebrew_formula', formData.homebrewFormula || '');
      fd.append('package_name', formData.packageName || '');
      fd.append('linux_distro', formData.linuxDistro || '');
      if (formData.file) fd.append('file', formData.file);
      const saved = isEdit && editingApp?.id
        ? await apiService.updateTenantApp(editingApp.id, fd)
        : await apiService.uploadTenantApp(fd);
      await onUpload(saved as Application);
    } catch (err: any) {
      window.alert(err?.message || (isEdit ? 'Failed to update application' : 'Failed to save application'));
    } finally {
      setUploading(false);
    }
  };

  const pkgMode = packageSettings?.source_mode || 'uem_hosted';
  const pkgPathLabel =
    pkgMode === 'client_share'
      ? packageSettings?.client_share_base || 'Your package share'
      : packageSettings?.uem_absolute_path || '/opt/uem/package-store/<tenant>';

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200 max-h-[90vh] flex flex-col">
        <div className="bg-slate-50 border-b border-slate-200 p-4 flex justify-between items-center shrink-0">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            {isEdit ? <Pencil size={18} className="text-slate-500" /> : <Upload size={18} className="text-slate-500" />}
            {isEdit ? 'Edit Application' : 'Upload Application'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <XCircle size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto">
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <p className="font-semibold text-slate-700 mb-0.5">
                Package location ({pkgMode === 'client_share' ? 'Own share' : 'UEM hosted'})
              </p>
              <p className="font-mono break-all">{pkgPathLabel}</p>
              {packageSettings?.quota_gb != null && pkgMode === 'uem_hosted' && (
                <p className="mt-1">Quota: {packageSettings.used_gb ?? 0} / {packageSettings.quota_gb} GB</p>
              )}
              {isEdit && (editingApp?.relativePath || editingApp?.fileType) && (
                <p className="mt-1 text-slate-500">
                  Current package:{' '}
                  <span className="font-mono">{editingApp?.relativePath || editingApp?.fileType}</span>
                  {editingApp?.fileSize != null ? ` (${Number(editingApp.fileSize).toFixed(1)} MB)` : ''}
                  {' — leave file empty to keep it'}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-2">Application Name</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Google Chrome"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">Version</label>
                <input
                  type="text"
                  required
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                  className="w-full border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., 120.0.6099.109"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">Platform</label>
                <select
                  required
                  value={formData.platform}
                  onChange={(e) => {
                    const newPlatform = e.target.value as 'windows' | 'macos' | 'linux' | 'android' | 'ios';
                    // Reset deployment method based on platform
                    let defaultMethod: 'file' | 'store' | 'winget' | 'homebrew' | 'package-manager' = 'file';
                    if (newPlatform === 'android' || newPlatform === 'ios') {
                      defaultMethod = 'store';
                    }
                    setFormData({ 
                      ...formData, 
                      platform: newPlatform,
                      deploymentMethod: defaultMethod,
                      // Clear platform-specific fields when platform changes
                      wingetId: '',
                      homebrewFormula: '',
                      packageName: '',
                      linuxDistro: '',
                      storeAppId: '',
                      installerUrl: '',
                      file: null
                    });
                    // Clear store search results
                    setStoreSearchResults([]);
                    setStoreSearchTerm('');
                  }}
                  className="w-full border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="windows">Windows</option>
                  <option value="macos">macOS</option>
                  <option value="linux">Linux</option>
                  <option value="android">Android</option>
                  <option value="ios">iOS</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-600 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full border border-slate-300 rounded-md p-2 h-24 resize-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Optional description..."
              />
            </div>

            {/* Deployment Method Selection - Platform Specific */}
            {formData.platform === 'windows' && (
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">Deployment Method</label>
                <select
                  value={formData.deploymentMethod}
                  onChange={(e) => {
                    const newMethod = e.target.value as any;
                    setFormData({ 
                      ...formData, 
                      deploymentMethod: newMethod,
                      // Clear fields when switching methods
                      wingetId: newMethod !== 'winget' ? '' : formData.wingetId,
                      installerUrl: newMethod !== 'file' ? '' : formData.installerUrl,
                      file: newMethod !== 'file' ? null : formData.file
                    });
                  }}
                  className="w-full border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="file">MSI/EXE File</option>
                  <option value="winget">Winget Package</option>
                </select>
              </div>
            )}

            {formData.platform === 'macos' && (
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">Deployment Method</label>
                <select
                  value={formData.deploymentMethod}
                  onChange={(e) => {
                    const newMethod = e.target.value as any;
                    setFormData({ 
                      ...formData, 
                      deploymentMethod: newMethod,
                      // Clear fields when switching methods
                      homebrewFormula: newMethod !== 'homebrew' ? '' : formData.homebrewFormula,
                      installerUrl: newMethod !== 'file' ? '' : formData.installerUrl,
                      file: newMethod !== 'file' ? null : formData.file
                    });
                  }}
                  className="w-full border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="file">PKG File</option>
                  <option value="homebrew">Homebrew Formula</option>
                </select>
              </div>
            )}

            {formData.platform === 'linux' && (
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">Deployment Method</label>
                <select
                  value={formData.deploymentMethod}
                  onChange={(e) => {
                    const newMethod = e.target.value as any;
                    setFormData({ 
                      ...formData, 
                      deploymentMethod: newMethod,
                      // Clear fields when switching methods
                      packageName: newMethod !== 'package-manager' ? '' : formData.packageName,
                      linuxDistro: newMethod !== 'package-manager' ? '' : formData.linuxDistro,
                      installerUrl: newMethod !== 'file' ? '' : formData.installerUrl,
                      file: newMethod !== 'file' ? null : formData.file
                    });
                  }}
                  className="w-full border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="file">DEB/RPM File</option>
                  <option value="package-manager">Package Manager (apt/yum)</option>
                </select>
              </div>
            )}

            {(formData.platform === 'android' || formData.platform === 'ios') && (
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">Deployment Method</label>
                <select
                  value={formData.deploymentMethod}
                  disabled
                  className="w-full border border-slate-300 rounded-md p-2 bg-slate-100 text-slate-600 cursor-not-allowed"
                >
                  <option value="store">App Store</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  {formData.platform === 'ios' ? 'iOS apps are deployed via App Store only' : 'Android apps are deployed via Play Store only'}
                </p>
              </div>
            )}

            {/* Store Search for Android/iOS */}
            {(formData.deploymentMethod === 'store' && (formData.platform === 'android' || formData.platform === 'ios')) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Search {formData.platform === 'ios' ? 'App Store' : 'Play Store'}
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={storeSearchTerm}
                    onChange={(e) => setStoreSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleStoreSearch())}
                    className="flex-1 border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={`Search for ${formData.platform === 'ios' ? 'iOS' : 'Android'} apps...`}
                  />
                  <button
                    type="button"
                    onClick={handleStoreSearch}
                    disabled={searchingStore || !storeSearchTerm.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {searchingStore ? 'Searching...' : 'Search'}
                  </button>
                </div>
                {storeSearchResults.length > 0 && (
                  <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-md bg-white">
                    {storeSearchResults.map((app, idx) => (
                      <div
                        key={idx}
                        onClick={() => selectStoreApp(app)}
                        className="p-3 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                      >
                        <div className="font-medium text-slate-800">
                          {formData.platform === 'ios' ? (app.trackName || app.collectionName) : (app.title || app.name)}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {formData.platform === 'ios' ? `ID: ${app.trackId}` : `Package: ${app.packageName || app.appId}`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {formData.storeAppId && (
                  <div className="mt-2 text-sm text-green-600 flex items-center gap-1">
                    <CheckCircle size={14} />
                    Selected: {formData.name} (ID: {formData.storeAppId})
                  </div>
                )}
              </div>
            )}

            {/* Winget ID for Windows */}
            {formData.deploymentMethod === 'winget' && formData.platform === 'windows' && (
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">Winget Package ID</label>
                <input
                  type="text"
                  required
                  value={formData.wingetId}
                  onChange={(e) => setFormData({ ...formData, wingetId: e.target.value })}
                  className="w-full border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Google.Chrome"
                />
                <p className="text-xs text-slate-500 mt-1">Find packages at: winget.run or winget search</p>
              </div>
            )}

            {/* Homebrew Formula for macOS */}
            {formData.deploymentMethod === 'homebrew' && formData.platform === 'macos' && (
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-2">Homebrew Formula/Cask</label>
                <input
                  type="text"
                  required
                  value={formData.homebrewFormula}
                  onChange={(e) => setFormData({ ...formData, homebrewFormula: e.target.value })}
                  className="w-full border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., google-chrome or --cask google-chrome"
                />
              </div>
            )}

            {/* Package Manager for Linux */}
            {formData.deploymentMethod === 'package-manager' && formData.platform === 'linux' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-2">Linux Distribution</label>
                  <select
                    required
                    value={formData.linuxDistro}
                    onChange={(e) => setFormData({ ...formData, linuxDistro: e.target.value as any })}
                    className="w-full border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Distribution</option>
                    <option value="debian">Debian (apt)</option>
                    <option value="ubuntu">Ubuntu (apt)</option>
                    <option value="rhel">RHEL (yum/dnf)</option>
                    <option value="centos">CentOS (yum/dnf)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-2">Package Name</label>
                  <input
                    type="text"
                    required
                    value={formData.packageName}
                    onChange={(e) => setFormData({ ...formData, packageName: e.target.value })}
                    className="w-full border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., google-chrome-stable or chrome"
                  />
                </div>
              </>
            )}

            {/* File Upload / Installer URL */}
            {formData.deploymentMethod === 'file' && (
              <>
                {pkgMode === 'client_share' ? (
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-2">
                      Path relative to share
                    </label>
                    <input
                      type="text"
                      value={formData.relativePath}
                      onChange={(e) => setFormData({ ...formData, relativePath: e.target.value })}
                      className="w-full border border-slate-300 rounded-md p-2 font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="windows/osquery.msi"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Resolved against {packageSettings?.client_share_base || 'your share base'}
                    </p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-2">
                      Application File {formData.installerUrl ? '(Optional if URL provided)' : ''}
                    </label>
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        required={!formData.installerUrl && !formData.relativePath}
                        onChange={(e) => setFormData({ ...formData, file: e.target.files?.[0] || null })}
                        className="hidden"
                        id="file-upload"
                        accept={
                          formData.platform === 'windows' ? '.msi,.exe' :
                          formData.platform === 'macos' ? '.pkg,.dmg' :
                          formData.platform === 'linux' ? '.deb,.rpm' :
                          formData.platform === 'android' ? '.apk' :
                          '.ipa'
                        }
                      />
                      <label
                        htmlFor="file-upload"
                        className="cursor-pointer flex flex-col items-center gap-2"
                      >
                        <Upload size={24} className="text-slate-400" />
                        <span className="text-sm text-slate-600">
                          {formData.file ? formData.file.name : 'Click to select installer'}
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-2">Installer URL (Optional)</label>
                  <input
                    type="url"
                    value={formData.installerUrl}
                    onChange={(e) => setFormData({ ...formData, installerUrl: e.target.value })}
                    className="w-full border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com/app.msi"
                  />
                  <p className="text-xs text-slate-500 mt-1">If set, agents download from this URL instead of the package share</p>
                </div>
              </>
            )}

            {/* Silent Installation Arguments */}
            {formData.deploymentMethod === 'file' && (formData.platform === 'windows' || formData.platform === 'macos' || formData.platform === 'linux') && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-2">
                    Silent Installation Arguments
                  </label>
                  <input
                    type="text"
                    value={formData.silentArgs}
                    onChange={(e) => setFormData({ ...formData, silentArgs: e.target.value })}
                    className="w-full border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={
                      formData.platform === 'windows' ? '/qn /norestart (MSI), /S (NSIS), -ms (Firefox)' :
                      formData.platform === 'macos' ? 'Leave empty for PKG installer' :
                      'Leave empty for dpkg/yum'
                    }
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {formData.platform === 'windows' ? 'MSI: /qn /norestart | EXE: /S (NSIS) | Firefox: -ms | Inno: /VERYSILENT' :
                     formData.platform === 'macos' ? 'PKG installer uses native installer command' :
                     'Package managers handle silently by default'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-2">Check Condition Type</label>
                    <select
                      value={formData.checkConditionType}
                      onChange={(e) => setFormData({ ...formData, checkConditionType: e.target.value as any })}
                      className="w-full border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">None</option>
                      <option value="file_path">File Path</option>
                      {formData.platform === 'windows' && <option value="registry_key">Registry Key</option>}
                      <option value="package_installed">Package Installed</option>
                      <option value="process_running">Process Running</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-600 mb-2">Check Value</label>
                    <input
                      type="text"
                      value={formData.checkConditionValue}
                      onChange={(e) => setFormData({ ...formData, checkConditionValue: e.target.value })}
                      disabled={!formData.checkConditionType}
                      className="w-full border border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-100"
                      placeholder={
                        formData.checkConditionType === 'file_path' ? 'C:\\Program Files\\App\\app.exe' :
                        formData.checkConditionType === 'registry_key' ? 'HKLM\\Software\\App' :
                        formData.checkConditionType === 'package_installed' ? 'package-name' :
                        formData.checkConditionType === 'process_running' ? 'process.exe' :
                        'Enter check value'
                      }
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={uploading}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {uploading ? 'Saving…' : isEdit ? 'Save Changes' : 'Upload Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Deployment Modal Component - Matching Policy Deployment Modal Style
const DeploymentModal = ({
  application,
  deviceGroups = [],
  devices = [],
  onClose,
  onDeploy
}: {
  application: Application;
  deviceGroups?: DeviceGroup[];
  devices?: any[];
  onClose: () => void;
  onDeploy: (deployment?: Deployment) => void | Promise<void>;
}) => {
  const [selectedTargetId, setSelectedTargetId] = useState('');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [targetType, setTargetType] = useState<'single' | 'multiple' | 'collection'>('single');
  const [deploying, setDeploying] = useState(false);

  const getDeviceId = (d: any) => pickerGetDeviceId(d);

  // Extract departments and platforms from deviceGroups
  const deptCategory = deviceGroups?.[0]?.children?.find((c: DeviceGroup) => c.id === 'cat-depts');
  const platformCategory = deviceGroups?.[0]?.children?.find((c: DeviceGroup) => c.id === 'cat-platforms');
  const departments = deptCategory?.children || [];
  const platforms = platformCategory?.children || [];

  // Get platform map for OS filtering
  const platformMap: { [key: string]: string } = {
    'windows': 'plat-windows',
    'android': 'plat-android',
    'macos': 'plat-macos',
    'linux': 'plat-linux',
    'ios': 'plat-ios'
  };
  const requiredPlatform = application.platform ? platformMap[application.platform] : null;

  const compatibleDevices = (devices || []).filter((d) => {
    if (!requiredPlatform && !application.platform) return true;
    return deviceMatchesOS(d, requiredPlatform || '') ||
      String(d.platform || '').toLowerCase() === String(application.platform || '').toLowerCase();
  });

  const handleConfirm = async () => {
    let finalTargetName = '';
    let deployTargetId = selectedTargetId;
    if (targetType === 'collection') {
      const selectedCollection = departments.find((d: DeviceGroup) => d.id === selectedTargetId) || 
                                 platforms.find((p: DeviceGroup) => p.id === selectedTargetId);
      finalTargetName = selectedCollection?.name || selectedTargetId;
    } else if (targetType === 'single') {
      const d = compatibleDevices.find((x) => getDeviceId(x) === selectedTargetId);
      finalTargetName = d ? `Device: ${d.name || selectedTargetId}` : `System ID: ${selectedTargetId}`;
    } else if (targetType === 'multiple') {
      deployTargetId = selectedDeviceIds.join(',');
      finalTargetName = `Multiple Systems: ${selectedDeviceIds.length} device(s)`;
    }

    const ids =
      targetType === 'single' && selectedTargetId
        ? [selectedTargetId]
        : targetType === 'multiple'
          ? selectedDeviceIds
          : [];

    if (ids.length === 0) {
      window.alert('Select at least one device to deploy');
      return;
    }

    setDeploying(true);
    try {
      const result = await apiService.deployTenantApp(application.id, {
        device_ids: ids,
        target_type: targetType === 'collection' ? 'group' : 'device',
        target_id: deployTargetId,
        target_name: finalTargetName,
        timeout_seconds: 600,
      });
      await onDeploy(result as Deployment);
    } catch (error) {
      console.error('Deployment error:', error);
      window.alert(`Deployment failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setDeploying(false);
    }
  };

  const canSubmit =
    (targetType === 'single' && !!selectedTargetId.trim()) ||
    (targetType === 'multiple' && selectedDeviceIds.length > 0) ||
    (targetType === 'collection' && !!selectedTargetId.trim());

  const getPlatformIcon = () => {
    switch (application.platform.toLowerCase()) {
      case 'windows':
        return <Monitor size={24} className="text-blue-600" />;
      case 'macos':
        return <Apple size={24} className="text-slate-600" />;
      case 'linux':
        return <Server size={24} className="text-orange-600" />;
      case 'android':
        return <Smartphone size={24} className="text-green-600" />;
      case 'ios':
        return <Tablet size={24} className="text-gray-600" />;
      default:
        return <Package size={24} className="text-blue-600" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200 max-h-[90vh] flex flex-col">
        <div className="bg-slate-50 border-b border-slate-200 p-4 flex justify-between items-center shrink-0">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2">
            <Package size={18} className="text-slate-500" />
            Deploy Application
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <XCircle size={20} />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto">
          {/* Application Header */}
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 rounded-lg bg-slate-100">
              {getPlatformIcon()}
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-bold text-slate-800">{application.name}</h4>
              <p className="text-sm text-slate-500 mt-1">{application.description || `Version ${application.version}`}</p>
              {application.platform && (
                <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                  <Shield size={12} />
                  {application.platform.toUpperCase()} Only
                </div>
              )}
            </div>
          </div>

          {/* Target Scope Section */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100 mb-6">
            <div className="flex justify-between items-center mb-3">
              <h5 className="text-xs font-bold text-blue-600 uppercase tracking-wide flex items-center gap-2">
                <Target size={12} /> Target Scope
              </h5>
            </div>
            
            <div className="space-y-3">
              <div className="flex gap-2 mb-2">
                <button onClick={() => setTargetType('single')} className={`flex-1 text-xs py-1.5 rounded border ${targetType === 'single' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300'}`}>Single</button>
                <button onClick={() => setTargetType('multiple')} className={`flex-1 text-xs py-1.5 rounded border ${targetType === 'multiple' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300'}`}>Multiple</button>
                <button onClick={() => setTargetType('collection')} className={`flex-1 text-xs py-1.5 rounded border ${targetType === 'collection' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300'}`}>Collection</button>
              </div>
              
              {targetType === 'collection' && (
                <>
                  {/* First: Select Folder */}
                  <div className="mb-3">
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Select Folder
                    </label>
                    <select 
                      className="w-full text-sm border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" 
                      onChange={(e) => {
                        const folderId = e.target.value || null;
                        setSelectedFolderId(folderId);
                        setSelectedTargetId(''); // Reset collection selection when folder changes
                      }} 
                      value={selectedFolderId || ''}
                    >
                      <option value="">-- Select Folder (Optional) --</option>
                      {departments.filter((d: DeviceGroup) => d.type === 'folder').map((folder: DeviceGroup) => (
                        <option key={folder.id} value={folder.id}>
                          {folder.name} ({folder.children?.length || 0} groups)
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Second: Select Collection/Group */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">
                      Select Group
                    </label>
                    <select 
                      className="w-full text-sm border-slate-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" 
                      onChange={(e) => setSelectedTargetId(e.target.value)} 
                      value={selectedTargetId}
                    >
                      <option value="">-- Select Target Group --</option>
                      
                      {/* Show collections from selected folder */}
                      {selectedFolderId && (() => {
                        const selectedFolder = departments.find((d: DeviceGroup) => d.id === selectedFolderId);
                        const folderCollections = selectedFolder?.children || [];
                        if (folderCollections.length > 0) {
                          return (
                            <optgroup label={`Groups in "${selectedFolder?.name}"`}>
                              {folderCollections.map((collection: DeviceGroup) => (
                                <option key={collection.id} value={collection.id}>
                                  {collection.name}
                                </option>
                              ))}
                            </optgroup>
                          );
                        }
                        return null;
                      })()}
                      
                      {/* Show root-level collections (not in folders) */}
                      {departments.filter((d: DeviceGroup) => d.type === 'dept' && !d.folderId).length > 0 && (
                        <optgroup label="Root Level Groups">
                          {departments
                            .filter((d: DeviceGroup) => d.type === 'dept' && !d.folderId)
                            .map((dept: DeviceGroup) => (
                              <option key={dept.id} value={dept.id}>
                                {dept.name} {dept.id !== 'Unassigned' && '(Group)'}
                              </option>
                            ))}
                        </optgroup>
                      )}
                      
                      {/* Platform Collections */}
                      {platforms.length > 0 && (
                        <optgroup label="By Platform">
                          {platforms.map((plat: DeviceGroup) => {
                            // Only show platform if it matches required platform or no requirement
                            if (requiredPlatform && plat.id !== requiredPlatform) return null;
                            return (
                              <option key={plat.id} value={plat.id}>
                                {plat.name} (Platform)
                              </option>
                            );
                          })}
                        </optgroup>
                      )}
                    </select>
                  </div>
                  
                  {requiredPlatform && (
                    <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                      <Info size={12} />
                      Only {application.platform.toUpperCase()} devices will be targeted from the selected group.
                    </p>
                  )}
                </>
              )}

              {targetType === 'single' && (
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Select Device</label>
                  <DevicePicker
                    devices={compatibleDevices}
                    value={selectedTargetId}
                    onChange={setSelectedTargetId}
                    platformFilter={application.platform || null}
                    emptyMessage="No compatible devices found for this client."
                  />
                </div>
              )}

              {targetType === 'multiple' && (
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Select Devices</label>
                  <DevicePicker
                    mode="multiple"
                    devices={compatibleDevices}
                    value={selectedDeviceIds}
                    onChange={setSelectedDeviceIds}
                    platformFilter={application.platform || null}
                    emptyMessage="No compatible devices available."
                  />
                </div>
              )}
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-600 font-medium hover:bg-slate-50 transition">Cancel</button>
            <button onClick={handleConfirm} disabled={!canSubmit || deploying} className="flex-1 py-2.5 bg-blue-600 rounded-lg text-white font-medium hover:bg-blue-700 shadow-lg shadow-blue-200 transition flex justify-center items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {deploying ? 'Deploying…' : 'Deploy Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Run Custom Script Tab Component
const RunCustomScriptTab = () => {
  const [devices, setDevices] = useState<any[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string>('');
  const [commandType, setCommandType] = useState<string>('shell');
  const [script, setScript] = useState<string>('');
  const [timeoutSeconds, setTimeoutSeconds] = useState<number>(60);
  const [loading, setLoading] = useState(false);
  const [commandHistory, setCommandHistory] = useState<any[]>([]);
  const [selectedCommand, setSelectedCommand] = useState<any | null>(null);

  useEffect(() => {
    loadDevices();
    loadCommandHistory();
  }, []);

  const loadDevices = async () => {
    try {
      const devicesData = await apiService.getDevices();
      setDevices(devicesData || []);
    } catch (error) {
      console.error('Failed to load devices:', error);
    }
  };

  const loadCommandHistory = async () => {
    if (!selectedDevice) return;
    try {
      const data = await apiService.getGoAgentCommands(selectedDevice, { limit: 50 });
      setCommandHistory(data.items || []);
    } catch (error) {
      console.error('Failed to load command history:', error);
    }
  };

  useEffect(() => {
    if (selectedDevice) {
      loadCommandHistory();
    }
  }, [selectedDevice]);

  const handleExecuteScript = async () => {
    if (!selectedDevice || !script.trim()) {
      alert('Please select a device and enter a script/command');
      return;
    }

    setLoading(true);
    try {
      // created_by is taken from the caller's token server-side.
      await apiService.executeGoAgentCommand(selectedDevice, {
        command_type: commandType,
        command: script,
        timeout_seconds: timeoutSeconds,
      });
      alert('Command executed successfully! Check command history for results.');
      setScript('');
      loadCommandHistory();
    } catch (error: any) {
      console.error('Failed to execute command:', error);
      alert(`Failed to execute command: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'failed':
        return <XCircle size={16} className="text-red-600" />;
      case 'running':
        return <Clock size={16} className="text-blue-600" />;
      default:
        return <Clock size={16} className="text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'failed':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'running':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const selectedDeviceInfo = devices.find(d => d.device_id === selectedDevice);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <h2 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
          <Terminal size={24} className="text-blue-600" />
          Remote Execution
        </h2>

        <div className="space-y-4">
          {/* Device Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Select Device
            </label>
            <DevicePicker
              devices={devices}
              value={selectedDevice}
              onChange={setSelectedDevice}
            />
            {selectedDeviceInfo && (
              <div className="mt-2 text-sm text-slate-600">
                <span className="font-medium">Status:</span> {selectedDeviceInfo.status} | 
                <span className="font-medium ml-2">Last Seen:</span> {new Date(selectedDeviceInfo.last_seen).toLocaleString()}
              </div>
            )}
          </div>

          {/* Command Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Command Type
            </label>
            <select
              value={commandType}
              onChange={(e) => setCommandType(e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="shell">Shell (Linux/macOS)</option>
              <option value="bash">Bash</option>
              <option value="powershell">PowerShell (Windows)</option>
              <option value="cmd">CMD (Windows)</option>
              <option value="python">Python</option>
              <option value="sql">SQL</option>
            </select>
          </div>

          {/* Script/Command Input */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Script / Command
            </label>
            <textarea
              value={script}
              onChange={(e) => setScript(e.target.value)}
              placeholder={`Enter your ${commandType} command or script here...\n\nExamples:\n- Linux: hostnamectl\n- Windows: Get-ComputerInfo\n- Python: print("Hello World")`}
              rows={8}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
            />
          </div>

          {/* Timeout */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Timeout (seconds)
            </label>
            <input
              type="number"
              value={timeoutSeconds}
              onChange={(e) => setTimeoutSeconds(parseInt(e.target.value) || 60)}
              min={10}
              max={3600}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Execute Button */}
          <div className="flex gap-3">
            <button
              onClick={handleExecuteScript}
              disabled={loading || !selectedDevice || !script.trim()}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Executing...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Execute Script
                </>
              )}
            </button>
            <button
              onClick={() => {
                setScript('');
                setSelectedCommand(null);
              }}
              className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Command History */}
      {selectedDevice && (
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Clock size={20} />
            Command History
          </h3>
          
          {commandHistory.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Terminal size={32} className="mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No commands executed yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {commandHistory.map((cmd) => (
                <div
                  key={cmd.command_id}
                  onClick={() => setSelectedCommand(cmd)}
                  className={`p-4 border rounded-lg cursor-pointer transition hover:shadow-md ${
                    selectedCommand?.command_id === cmd.command_id
                      ? 'ring-2 ring-blue-500 border-blue-500'
                      : getStatusColor(cmd.status)
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusIcon(cmd.status)}
                        <span className="font-medium text-sm">{cmd.command_type.toUpperCase()}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(cmd.status)}`}>
                          {cmd.status}
                        </span>
                      </div>
                      <div className="font-mono text-sm text-slate-700 bg-slate-50 p-2 rounded mb-2">
                        {cmd.command}
                      </div>
                      <div className="text-xs text-slate-500">
                        Created: {new Date(cmd.created_at).toLocaleString()}
                        {cmd.executed_at && ` | Executed: ${new Date(cmd.executed_at).toLocaleString()}`}
                        {cmd.completed_at && ` | Completed: ${new Date(cmd.completed_at).toLocaleString()}`}
                      </div>
                    </div>
                  </div>
                  
                  {selectedCommand?.command_id === cmd.command_id && cmd.result && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <div className="text-xs font-medium text-slate-600 mb-1">Result:</div>
                      <pre className="text-xs bg-slate-50 p-3 rounded overflow-auto max-h-64 font-mono">
                        {typeof cmd.result === 'object' 
                          ? JSON.stringify(cmd.result, null, 2)
                          : cmd.result}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApplicationDeployment;

